import React, { useState, useEffect } from 'react'
import { storeAuthToken, storeOrganizerId, storeUserDetails } from '../../utils/auth'
import { Link, useNavigate } from 'react-router-dom'
import { loginRequestOTP, userLogin, loginVerifyOTP, getOrganizerProfile } from '../../api'
import { useDispatch } from 'react-redux'
import PinInput from "react-pin-input"
import { ShieldCheck, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { images } from '../../assets/images'
import Background from './Background'
import { useNightMode } from '../../contexts/NightModeContext';
import './LoginPage.css'

type LoginPageProps = {}

/** Map raw API / validation strings into generic login copy (no role hints). */
const formatOrganizerLoginError = (raw?: string | null, fallback = "Unable to sign in with these credentials. Please check your details and try again.") => {
  const msg = String(raw || "").trim();
  if (!msg) return fallback;
  const lower = msg.toLowerCase();

  if (
    lower.includes("user not found") ||
    lower.includes("no user") ||
    lower.includes("invalid password") ||
    lower.includes("incorrect password") ||
    lower.includes("wrong password") ||
    lower.includes("invalid username or password") ||
    lower.includes("invalid credentials") ||
    lower.includes("access denied") ||
    lower.includes("not authorized") ||
    lower.includes("not allowed") ||
    lower.includes("venue") ||
    lower.includes("organizer")
  ) {
    return "Unable to sign in with these credentials. Please check your details and try again.";
  }
  if (lower.includes("social login")) {
    return "Unable to sign in with these credentials. Please check your details and try again.";
  }
  if (lower.includes("internal server")) {
    return "Something went wrong on our side. Please wait a moment and try again.";
  }
  if (lower === "please enter your email") {
    return "Enter your email to continue.";
  }
  if (lower === "please enter your password") {
    return "Enter your password to continue.";
  }
  return msg;
};

const LoginErrorAlert = ({ message }: { message: string }) => (
  <div className="login-error-alert" role="alert">
    <AlertCircle size={18} className="login-error-alert-icon" aria-hidden />
    <div className="login-error-alert-body">
      <span className="login-error-alert-title">Unable to sign in</span>
      <p className="login-error-alert-text">{message}</p>
    </div>
  </div>
);

const LoginPage = (props: LoginPageProps) => {
	const dispatch = useDispatch();
	let navigate = useNavigate()
	const [username, setUsername] = useState("")
	const [password, setPassword] = useState("")
	const [errorMsg, setErrorMsg] = useState("")
	const [isLoading, setIsLoading] = useState(false)
	const [showPassword, setShowPassword] = useState(false)
  const [loginWithPassword, setLoginWithPassword] = useState(true) 
  const [otp, setOtp] = useState("")
  const [timer, setTimer] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [email,setEmail] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [pendingResponse, setPendingResponse] = useState<any>(null);

  const roleLabels: { [key: string]: string } = {
    "2": "Organizer Dash Board",
    "10": "Co Organizer",
    "8": "Event co organizer"
  };

  const showLoginError = (raw?: string | null, fallback?: string) => {
    setErrorMsg(formatOrganizerLoginError(raw, fallback));
  };

  const handleFinalLogin = async (response: any, selectedRole?: string) => {
    if (selectedRole) {
      response.user.userRole = selectedRole;
    }
    const userRole = response.user.userRole;
    storeAuthToken(response.token)
    storeUserDetails(response.user)
    
    if (userRole === "8" || userRole === "10" || userRole === "2") {
      try {
        const orgProfile = await getOrganizerProfile();
        localStorage.setItem("organizerData", JSON.stringify(orgProfile));
      } catch (e) {
        console.error("Failed to fetch organizer profile", e);
      }
    }

    if (userRole === "8" || userRole === "10") {
      storeOrganizerId(Number(response?.user?.organizerId))
    } else {
      storeOrganizerId(Number(response?.user?.userId))
    }
    if (userRole === "4") {
      const eventId = response.user.venue.event.eventId;
      const venueId = response.user.venue.venueId;
      const eventData = response.user.venue.event;
      const userData = response.user;
      localStorage.setItem("eventData", JSON.stringify(eventData));
      if (venueId) {
        localStorage.setItem("venueId", venueId);
        localStorage.setItem("userData", JSON.stringify(userData));
      }
      if (eventId) {
        navigate(`/venue-event/${eventId}`);
      }
    } else {
      navigate('/home');
    }
  };
  // OTP APIs sometimes return { message } and sometimes { error } — normalize both.
  const getApiErrorMessage = (err: any, fallback: string) =>
    err?.response?.data?.message || err?.response?.data?.error || fallback;

  const handleSendOtp = async () => {
    if (!username) {
      showLoginError("Please enter your email");
      return;
    }
    try {
      // Use isLoading so the Send OTP button shows a loading state / stays disabled.
      setIsLoading(true);
      setError("");
      setErrorMsg("");
      const response = await loginRequestOTP({ email: username });
      console.log("OTP request response:", response);
      if (!response?.registrationId) {
        setError(formatOrganizerLoginError(null, "Failed to start OTP session. Please try again."));
        return;
      }
      setRegistrationId(response.registrationId);
      if (response.email) {
        setEmail(response.email);
      }
      // Only show the OTP input once we actually have a session id.
      setOtpSent(true);
      setTimer(60);
    } catch (err:any) {
      console.error("Failed to request OTP:", err);
      setError(formatOrganizerLoginError(getApiErrorMessage(err, "Something went wrong")));
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendOtp = async () => {
    if (!username) {
      showLoginError("Please enter your email");
      return;
    }
    try {
      setIsLoading(true);
      setError("");
      setErrorMsg("");
      const response = await loginRequestOTP({ email: username });
      console.log("OTP request response:", response);
      if (!response?.registrationId) {
        showLoginError(null, "Failed to resend OTP. Please try again.");
        return;
      }
      setRegistrationId(response.registrationId);
      setOtp("");
      setTimer(60);
    } catch (err:any) {
      console.error("Failed to request OTP:", err);
      showLoginError(getApiErrorMessage(err, "Something went wrong"));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      navigate('/home');
    }
  }, [navigate]);
  const { isNightMode } = useNightMode();

  useEffect(() => {
    document.documentElement.classList.add('night-mode');
  }, []);

  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
    };
  }, []);
  const onClickOtpLogin =async () => {
    setErrorMsg("");
    if (!username) {
      showLoginError("Please enter your email");
      return;
    }
    if (!registrationId) {
      showLoginError(null, "OTP session expired. Request a new code and try again.");
      return;
    }
    if (otp.length !== 6) {
      showLoginError(null, "Enter the full 6-digit code sent to your email.");
      return;
    }
    setIsLoading(true)
    console.log("OTP Login button clicked")
    // Prefer the email returned by request-otp when available (normalized by API).
    let data = {
      email: email || username,
      "registrationId": registrationId,
      "otp": otp
    }
    try {
      const response: any = await loginVerifyOTP(data);
      if (response) {
        // Guard against unexpected response shapes so we don't fail silently.
        if (!response.user || !response.token) {
          showLoginError(null, "Unexpected login response. Please try again.");
          return;
        }

        // Normalize roles to strings — OTP API may return numbers while password login returns strings.
        const userRole = String(response.user.userRole);
        const userRoles = (response.user.userRoles || []).map((role: string | number) => String(role));
        console.log("Login success", response)

        const rolesToCheck = ["2", "8", "10"];
        const foundRoles = rolesToCheck.filter(role =>
          userRole === role || userRoles.includes(role)
        );

        if (foundRoles.length === 0 && userRole !== "4") {
          showLoginError(null, "Unable to sign in with these credentials. Please check your details and try again.");
          return;
        }

        if (foundRoles.length > 1) {
          setAvailableRoles(foundRoles);
          setPendingResponse(response);
          setShowRoleSelector(true);
        } else {
          await handleFinalLogin(response, foundRoles[0] || userRole);
        }
      }

    } catch (e:any) {
      // verify-otp often returns { error } on 500s, not { message } like /auth/login.
      showLoginError(getApiErrorMessage(e, "OTP verification failed. Please try again."));
      console.log("Login failed", e)
    }finally{
      setIsLoading(false)
    }
  }
	const onClickLogin =async () => {
    setErrorMsg("")
    if (!username) {
      showLoginError("Please enter your email")
      return
    }
    if (!password) {
      showLoginError("Please enter your password")
      return
    }
		setIsLoading(true)
		console.log("Login button clicked")
		let data = {
			"username": username,
			"password": password
		}
		try {
			const response: any = await userLogin(data);
			if (response) { 
        const userRole = response.user.userRole;
        const userRoles = response.user.userRoles || [];
				console.log("Login success", response)

        const rolesToCheck = ["2", "8", "10"];
        const foundRoles = rolesToCheck.filter(role => 
          userRole === role || userRoles.includes(role)
        );

        if (foundRoles.length === 0 && userRole !== "4") {
          showLoginError(null, "Unable to sign in with these credentials. Please check your details and try again.");
          setIsLoading(false);
          return;
        }

        if (foundRoles.length > 1) {
          setAvailableRoles(foundRoles);
          setPendingResponse(response);
          setShowRoleSelector(true);
          setIsLoading(false);
        } else {
          await handleFinalLogin(response, foundRoles[0] || userRole);
        }
      }

		} catch (e:any) {
			showLoginError(getApiErrorMessage(e, "Sign-in failed. Please try again."));
			console.log("Login failed", e)
		}finally{
			setIsLoading(false)
		}
	}

  const activeError = errorMsg || error;

	return (
    <div id="login-screen-wrapper">
      <Background />

      <div className="login-card">
        {/* Logo */}
        <div className="logo-container">
          <a href="index.html">
            <img src={images.eventox_dark_logo} alt="Eventox Logo" className="logo-image" />
          </a>
        </div>

        <p className="login-subtitle">
          Premium Organizer Administration & Box Office Engine
        </p>

        {/* Secure indicator */}
        <div className="secure-badge">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" style={{ color: '#18C37E' }} />
          <span>Authorized Enterprise Directory Node active. Single Sign-On configured.</span>
        </div>

        {/* Main Content Area */}
        {showRoleSelector ? (
          <div className="role-selector-container">
            <h2 className="registration-title text-center mb-2" style={{ fontSize: '17px', fontWeight: 'bold',color: 'white' }}>Select Your Role</h2>
            <p className="text-center mb-3" style={{ fontSize: '12px', color: 'var(--login-text-secondary)' }}>Please select the role you want to login with</p>
            <div className="role-list">
              {availableRoles.map((role) => (
                <div
                  key={role}
                  className="role-item"
                  onClick={async () => await handleFinalLogin(pendingResponse, role)}
                >
                  {roleLabels[role] || `Role ${role}`}
                </div>
              ))}
            </div>
            <div
              className="role-back-btn"
              onClick={() => setShowRoleSelector(false)}
            >
              Back to Login
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => e.preventDefault()} className="login-form">
            {/* Email field */}
            <div className="input-group-custom">
              <label className="label-custom">Your Email*</label>
              <div className="input-wrapper">
                <span className="input-icon-left">
                  <Mail size={16} />
                </span>
                <input
                  className="input-custom"
                  type="email"
                  placeholder="Enter your email"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (errorMsg || error) {
                      setErrorMsg("");
                      setError("");
                    }
                  }}
                />
              </div>
            </div>

            {/* Password Login Mode */}
            {loginWithPassword ? (
              <div className="login-form" style={{ gap: '1.25rem' }}>
                <div className="input-group-custom">
                  <div className="password-label-row">
                    <label className="label-custom">Password*</label>
                    <Link className="forgot-link-custom" to="/forgot-password">
                      Forgot Password?
                    </Link>
                  </div>
                  <div className="input-wrapper">
                    <span className="input-icon-left">
                      <Lock size={16} />
                    </span>
                    <input
                      className="input-custom"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errorMsg || error) {
                          setErrorMsg("");
                          setError("");
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {activeError && <LoginErrorAlert message={activeError} />}

                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    onClickLogin();
                  }}
                >
                  {isLoading ? "Signing In..." : "Sign In to Eventox Workspace"}
                </button>
              </div>
            ) : (
              /* OTP Login Mode */
              <div className="login-form" style={{ gap: '1.25rem' }}>
                {!otpSent ? (
                  <div className="login-form" style={{ gap: '1.25rem' }}>
                    {activeError && <LoginErrorAlert message={activeError} />}
                    <button
                      type="button"
                      className="btn-submit"
                      disabled={isLoading}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSendOtp();
                      }}
                    >
                      {isLoading ? "Sending OTP..." : "Send OTP"}
                    </button>
                    <div className="register-link-custom">
                      New to EventoX? <a href="/register">Sign up</a>
                    </div>
                  </div>
                ) : (
                  <div className="login-form" style={{ gap: '1rem' }}>
                    <div className="input-group-custom">
                      <label className="label-custom" style={{ textAlign: 'center', width: '100%' }}>Enter OTP*</label>
                      <div className="otp-input-container">
                        <PinInput
                          length={6}
                          secret={false}
                          type="numeric"
                          inputStyle={{
                            width: "40px",
                            height: "48px",
                            margin: "0 6px",
                            borderRadius: "10px",
                            border: "1px solid var(--login-input-border)",
                            background: "var(--login-input-bg)",
                            color: "var(--login-text-primary)",
                            fontSize: "20px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                          onChange={(value) => setOtp(value)}
                          onComplete={(value) => setOtp(value)}
                        />
                      </div>
                    </div>
                    

                    <div className="text-center" style={{ fontSize: '13px' }}>
                      {timer > 0 ? (
                        <p style={{ color: 'var(--login-text-muted)', margin: 0 }}>
                          Re-send available in {timer}s
                        </p>
                      ) : (
                        <p style={{ color: 'var(--login-text-secondary)', margin: 0 }}>
                          Didn’t receive code?{" "}
                          <span
                            style={{ cursor: "pointer", fontWeight: "700", color: "#EE3F7C" }}
                            onClick={handleResendOtp}
                          >
                            Resend
                          </span>
                        </p>
                      )}
                    </div>

                    {activeError && <LoginErrorAlert message={activeError} />}

                    <button
                      type="submit"
                      className="btn-submit"
                      disabled={isLoading}
                      onClick={(e) => {
                        e.preventDefault();
                        onClickOtpLogin();
                      }}
                    >
                      {isLoading ? "Verifying..." : "Verify & Sign In"}
                    </button>
                  
                  </div>
                )}
              </div>
            )}

            {/* Toggle between Password and OTP */}
            <div className="toggle-container">
              <div
                className={`toggle-option ${loginWithPassword ? "active" : ""}`}
                onClick={() => {
                  setLoginWithPassword(true);
                  setErrorMsg("");
                  setError("");
                }}
              >
                Email and Password
              </div>
              <div
                className={`toggle-option ${!loginWithPassword ? "active" : ""}`}
                onClick={() => {
                  setLoginWithPassword(false);
                  setErrorMsg("");
                  setError("");
                }}
              >
                Email OTP
              </div>
            </div>
            <div className="register-link-custom">
                  New to EventoX? <a href="/register">Sign up</a>
                </div>
          </form>
        )}
        <div className="text-center" style={{ fontSize: '11px', color: 'var(--login-text-muted)', marginTop: '-0.25rem' }}>
          © {new Date().getFullYear()}, Eventox. All rights reserved.
        </div>
      </div>
    </div>
  );
}

export default LoginPage
